/**
 * Action Items List Component
 * 
 * Displays action items for a case with status indicators and grouping.
 * Part of the 90% Automation System visibility UI.
 */

import React, { useState, useEffect } from 'react';
import { FileText } from 'lucide-react';
import { apiFetch } from '../../utils/api';

interface ActionItem {
  id: string;
  report_id: string;
  source_agent: string;
  action_type: string;
  urgency: string;
  is_blocking: boolean;
  title: string;
  description: string;
  reason: string;
  document_type?: string;
  document_period?: string;
  status: string;
  created_at: string;
  request_count: number;
}

interface ActionItemsListProps {
  reportId: string;
  compact?: boolean;
  showResolved?: boolean;
  maxItems?: number;
}

export const ActionItemsList: React.FC<ActionItemsListProps> = ({
  reportId,
  compact = false,
  showResolved = false,
  maxItems = 10
}) => {
  const [items, setItems] = useState<ActionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchItems = async () => {
      try {
        setLoading(true);
        const response = await apiFetch(`/api/v1/action-items/${reportId}`);
        const data = response.data || response;
        setItems(data.items || []);
      } catch (err) {
        console.error('Failed to fetch action items:', err);
        setError('Failed to load action items');
      } finally {
        setLoading(false);
      }
    };

    if (reportId) {
      fetchItems();
    }
  }, [reportId]);

  const getUrgencyColor = (urgency: string): React.CSSProperties => {
    switch (urgency.toLowerCase()) {
      case 'critical': return { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', borderColor: 'var(--color-needs-human-tint-bd)' };
      case 'high': return { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-brand)', borderColor: 'var(--color-needs-human-tint-bd)' };
      case 'medium': return { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-brand)', borderColor: 'var(--color-needs-human-tint-bd)' };
      case 'low': return { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)', borderColor: 'var(--color-border)' };
      default: return { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)', borderColor: 'var(--color-border)' };
    }
  };

  const getStatusColor = (status: string): React.CSSProperties => {
    switch (status.toLowerCase()) {
      case 'pending': return { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' };
      case 'in_progress': return { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-brand)' };
      case 'resolved': return { backgroundColor: 'transparent', color: 'var(--color-verified)' };
      case 'cancelled': return { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' };
      default: return { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' };
    }
  };

  const getSourceAgentIcon = (agent: string) => {
    switch (agent.toLowerCase()) {
      case 'forensic_accountant':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        );
      case 'attorney':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
          </svg>
        );
      case 'adjuster_analyzer':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        );
    }
  };

  const filteredItems = showResolved 
    ? items 
    : items.filter(item => item.status !== 'resolved' && item.status !== 'cancelled');

  const displayItems = filteredItems.slice(0, maxItems);
  const blockingItems = displayItems.filter(item => item.is_blocking);
  const nonBlockingItems = displayItems.filter(item => !item.is_blocking);

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-16 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm p-3 rounded-lg" style={{ color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)' }}>
        {error}
      </div>
    );
  }

  if (filteredItems.length === 0) {
    return (
      <div className="text-center py-6" style={{ color: 'var(--color-text-muted)' }}>
        <svg className="w-12 h-12 mx-auto mb-2" style={{ color: 'var(--color-verified)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-sm">No pending action items</p>
      </div>
    );
  }

  const renderItem = (item: ActionItem) => (
    <div
      key={item.id}
      className={`rounded-lg border p-3 ${compact ? 'p-2' : 'p-3'}`}
      style={item.is_blocking ? { borderColor: 'var(--color-needs-human-tint-bd)', backgroundColor: 'var(--color-needs-human-tint)' } : { borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-surface)' }}
    >
      <div className="flex items-start gap-3">
        {/* Agent Icon */}
        <div className="p-2 rounded-full" style={{ backgroundColor: item.is_blocking ? 'var(--color-needs-human-tint)' : 'var(--color-bg-elevated)' }}>
          {getSourceAgentIcon(item.source_agent)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>{item.title}</span>
            {item.is_blocking && (
              <span className="text-xs px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}>
                BLOCKING
              </span>
            )}
          </div>
          {!compact && (
            <p className="text-sm mt-1 line-clamp-2" style={{ color: 'var(--color-text-secondary)' }}>{item.description}</p>
          )}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className="text-xs px-2 py-0.5 rounded-full border" style={getUrgencyColor(item.urgency)}>
              {item.urgency}
            </span>
            <span className="text-xs px-2 py-0.5 rounded-full" style={getStatusColor(item.status)}>
              {item.status.replace('_', ' ')}
            </span>
            {item.document_type && (
              <span className="text-xs inline-flex items-center gap-1" style={{ color: 'var(--color-text-muted)' }}>
                <FileText size={11} /> {item.document_type.replace('_', ' ')}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="flex items-center justify-between text-sm">
        <span style={{ color: 'var(--color-text-secondary)' }}>
          {filteredItems.length} action item{filteredItems.length !== 1 ? 's' : ''}
        </span>
        {blockingItems.length > 0 && (
          <span className="font-medium" style={{ color: 'var(--color-needs-human-text)' }}>
            {blockingItems.length} blocking
          </span>
        )}
      </div>

      {/* Blocking Items */}
      {blockingItems.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium flex items-center gap-1" style={{ color: 'var(--color-needs-human-text)' }}>
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            Blocking Items
          </h4>
          {blockingItems.map(renderItem)}
        </div>
      )}

      {/* Other Items */}
      {nonBlockingItems.length > 0 && (
        <div className="space-y-2">
          {blockingItems.length > 0 && (
            <h4 className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Other Items</h4>
          )}
          {nonBlockingItems.map(renderItem)}
        </div>
      )}

      {/* Show More */}
      {filteredItems.length > maxItems && (
        <div className="text-center pt-2">
          <span className="text-sm cursor-pointer hover:underline" style={{ color: 'var(--color-needs-human-text)' }}>
            +{filteredItems.length - maxItems} more items
          </span>
        </div>
      )}
    </div>
  );
};

export default ActionItemsList;
